/**
 * LightCookies — projected light textures (gobos) for spotlights.
 *
 * three r185's SpotLightNode natively projects `light.map` through the
 * light's view-projection (and accepts a procedural `light.colorNode(coord)`)
 * — this module is the engine-facing wrapper: texture conditioning (cookies
 * sample in light space: no flips, clamp, sRGB for colour gobos / linear for
 * masks), assignment, and a procedural window/blinds generator for instant
 * use without an asset.
 */

import * as THREE from 'three/webgpu';

export interface CookieOptions {
  /** Treat as colour (sRGB) vs mask (linear). Default 'mask'. */
  kind?: 'color' | 'mask';
}

/**
 * Assign a cookie texture to a spotlight. Returns a remover. The spotlight
 * MUST have a map-capable penumbra (any three spotlight works; the node
 * backend samples the map inside the cone attenuation).
 */
export function applyLightCookie(
  light: THREE.SpotLight,
  texture: THREE.Texture,
  options?: CookieOptions,
): () => void {
  texture.colorSpace = options?.kind === 'color' ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  // Light-space projection: the map is addressed by the light frustum, not
  // screen/uv space — flipY would mirror the gobo.
  texture.flipY = false;
  texture.needsUpdate = true;
  (light as unknown as { map: THREE.Texture | null }).map = texture;
  return () => {
    if ((light as unknown as { map: THREE.Texture | null }).map === texture) {
      (light as unknown as { map: THREE.Texture | null }).map = null;
    }
  };
}

/**
 * Procedural window/blinds cookie: `bars` dark slats across a bright pane
 * with a soft border — the classic "light through blinds" gobo. CPU-baked
 * (tiny), returned as a ready DataTexture.
 */
export function createBlindsCookie(size = 128, bars = 6, softness = 0.08): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / (size - 1);
      const v = y / (size - 1);
      const value = blindsCookieValueCpu(u, v, bars, softness);
      const i = (y * size + x) * 4;
      const b = Math.round(value * 255);
      data[i] = b; data[i + 1] = b; data[i + 2] = b; data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.flipY = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  tex.name = 'BlindsCookie';
  return tex;
}

/** CPU mirror of the blinds pattern (exported for tests). */
export function blindsCookieValueCpu(
  u: number, v: number, bars: number, softness: number,
): number {
  // Soft border falloff.
  const edge = Math.min(u, 1 - u, v, 1 - v);
  const border = clamp01(edge / Math.max(softness, 1e-6));
  // Horizontal slats: dark where the slat sits.
  const slat = Math.abs(Math.sin(v * bars * Math.PI));
  const slatMask = clamp01((slat - 0.35) / 0.3);
  return border * slatMask;
}

function clamp01(x: number): number { return x < 0 ? 0 : (x > 1 ? 1 : x); }
